import React, { useState, useEffect } from 'react';
import { useTheme } from '../contexts/ThemeContext';
import { useAuth } from '../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import { getUserGoals } from '../services/firebase/userGoalsService';
import { doc, updateDoc, getDoc } from 'firebase/firestore';
import { db } from '../firebase';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts';
import { 
  FaSitemap, 
  FaProjectDiagram,
  FaChartLine,
  FaBook,
  FaUserTie,
  FaFlag,
  FaBullseye,
  FaCogs,
  FaCalendarAlt,
  FaUserCog,
  FaTimes,
  FaArrowUp,
  FaArrowDown,
  FaPlus,
  FaLock
} from 'react-icons/fa';
import StartSidebar from '../components/StartSidebar';

const GPMOperations = () => {
  const { darkMode } = useTheme();
  const { currentUser } = useAuth();
  const navigate = useNavigate();
  
  // State for user goals
  const [userGoals, setUserGoals] = useState(null);
  const [goalsLoading, setGoalsLoading] = useState(true);
  
  // GPM form state
  const [gpmForm, setGpmForm] = useState({
    twoYearsAgo: '',
    oneYearAgo: '',
    currentYearProjection: ''
  });
  const [showGpmForm, setShowGpmForm] = useState(true);
  const [gpmData, setGpmData] = useState(null);

  // Mock data for goals and roadmap
  const currentProject = {
    title: 'Q1 GPM Optimization',
    progress: 85,
    description: 'Improving gross profit margins through operational efficiency'
  };

  // Helper function to format goal values
  const formatGoalValue = (value, unit) => {
    if (value === null || value === undefined) return '0';
    
    if (unit === 'usd') {
      return `$${value.toLocaleString()}`;
    } else if (unit === 'percent') {
      return `${value}%`;
    } else {
      return value.toString();
    }
  };

  const operationsItems = [
    { 
      title: 'Org Chart', 
      icon: <FaSitemap />, 
      description: 'View and manage your organization structure'
    },
    {
      title: 'Job Descriptions',
      icon: <FaUserTie />,
      description: 'View and manage role descriptions and responsibilities'
    },
    {
      title: 'SOPs',
      icon: <FaBook />,
      description: 'Access and manage Standard Operating Procedures for all business processes'
    },
    {
      title: 'KPIs',
      icon: <FaChartLine />,
      description: 'Track and monitor key performance indicators across departments'
    }
  ];

  // Load user goals from Firestore
  const loadUserGoals = async () => {
    if (!currentUser) return;
    
    try {
      setGoalsLoading(true);
      const goals = await getUserGoals(currentUser.uid);
      setUserGoals(goals);
    } catch (error) {
      console.error('Error loading user goals:', error);
    } finally {
      setGoalsLoading(false);
    }
  };

  // Load existing GPM data from Firestore
  const loadExistingGpmData = async () => {
    if (!currentUser) return;
    
    try {
      const userDoc = await getDoc(doc(db, 'users', currentUser.uid));
      if (userDoc.exists()) {
        const userData = userDoc.data();
        const gpmData = userData.businessAssessment?.gpmData;
        
        if (gpmData) {
          // Set the form data
          setGpmForm({
            twoYearsAgo: gpmData.twoYearsAgo?.toString() || '',
            oneYearAgo: gpmData.oneYearAgo?.toString() || '',
            currentYearProjection: gpmData.currentYearProjection?.toString() || ''
          });
          
          // Set the GPM data for chart display
          setGpmData(gpmData);
          
          // Hide the form if data exists
          setShowGpmForm(false);
        }
      }
    } catch (error) {
      console.error('Error loading existing GPM data:', error);
    }
  };

  // Save GPM data to Firestore
  const saveGpmData = async () => {
    if (!currentUser) return;
    
    try {
      const gpmDataToSave = {
        twoYearsAgo: gpmForm.twoYearsAgo ? parseInt(gpmForm.twoYearsAgo) : null,
        oneYearAgo: gpmForm.oneYearAgo ? parseInt(gpmForm.oneYearAgo) : null,
        currentYearProjection: gpmForm.currentYearProjection ? parseInt(gpmForm.currentYearProjection) : null
      };
      
      await updateDoc(doc(db, 'users', currentUser.uid), {
        'businessAssessment.gpmData': gpmDataToSave,
        lastUpdated: new Date().toISOString()
      });
      
    } catch (error) {
      console.error('Error saving GPM data:', error);
    }
  };

  // Handle GPM form input changes
  const handleGpmFormChange = (e) => {
    const { name, value } = e.target;
    // Remove any non-numeric characters and convert to number
    const numericValue = value.replace(/[^0-9]/g, '');
    setGpmForm(prev => ({
      ...prev,
      [name]: numericValue
    }));
    
    // Save data immediately after a short delay
    setTimeout(() => saveGpmData(), 500);
  };

  // Handle GPM form submission
  const handleGpmSubmit = async () => {
    if (!gpmForm.twoYearsAgo || !gpmForm.oneYearAgo || !gpmForm.currentYearProjection) {
      alert('Please fill in all GPM fields');
      return;
    }

    const currentYear = new Date().getFullYear();
    const chartData = [
      { year: currentYear - 2, value: parseInt(gpmForm.twoYearsAgo) },
      { year: currentYear - 1, value: parseInt(gpmForm.oneYearAgo) },
      { year: currentYear, value: parseInt(gpmForm.currentYearProjection) }
    ];

    const gpmDataToSave = {
      twoYearsAgo: parseInt(gpmForm.twoYearsAgo),
      oneYearAgo: parseInt(gpmForm.oneYearAgo),
      currentYearProjection: parseInt(gpmForm.currentYearProjection),
      chartData
    };

    setGpmData(gpmDataToSave);
    setShowGpmForm(false);

    // Save to Firestore
    if (currentUser) {
      try {
        await updateDoc(doc(db, 'users', currentUser.uid), {
          'businessAssessment.gpmData': gpmDataToSave,
          'operationsData.gpmData': gpmDataToSave,
          'operationsData.twoYearsAgo': gpmDataToSave.twoYearsAgo,
          'operationsData.oneYearAgo': gpmDataToSave.oneYearAgo,
          'operationsData.currentYearProjection': gpmDataToSave.currentYearProjection,
          lastUpdated: new Date().toISOString()
        });
        
      } catch (error) {
        console.error('Error saving GPM data:', error);
      }
    }
  };

  // Handle edit button click
  const handleEditGpm = () => {
    setShowGpmForm(true);
  };

  // Handle continue button click
  const handleContinue = async () => {
    if (!currentUser) return;
    
    try {
      // Save GPM completion status to Firestore in both businessAssessment and operationsData collections
      await updateDoc(doc(db, 'users', currentUser.uid), {
        'businessAssessment.gpmCompleted': true,
        'businessAssessment.gpmData': gpmData,
        'operationsData.gpmCompleted': true,
        'operationsData.gpmData': gpmData,
        'operationsData.twoYearsAgo': gpmData.twoYearsAgo,
        'operationsData.oneYearAgo': gpmData.oneYearAgo,
        'operationsData.currentYearProjection': gpmData.currentYearProjection,
        'businessAssessmentProgress.currentSection': 4, // Set to Operations section (index 4)
        lastUpdated: new Date().toISOString()
      });
      
      
      // Navigate back to existing conditions page with state to indicate we came from GPM
      navigate('/start/existing-conditions', { state: { fromGPMOperations: true } });
    } catch (error) {
      console.error('Error saving GPM completion:', error);
      alert('Error saving data. Please try again.');
    }
  };

  // Helper function to generate chart data
  const generateGpmChartData = () => {
    if (!gpmData || !gpmData.chartData) {
      return [
        { year: new Date().getFullYear() - 2, value: 0 },
        { year: new Date().getFullYear() - 1, value: 0 },
        { year: new Date().getFullYear(), value: 0 }
      ];
    }

    return gpmData.chartData;
  };

  // Scroll to top when component mounts
  useEffect(() => {
    window.scrollTo({
      top: 0,
      behavior: 'smooth'
    });
  }, []);

  // Load data on component mount
  useEffect(() => {
    loadUserGoals();
    loadExistingGpmData();
  }, [currentUser]);

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900">
      <StartSidebar />
      <div className="p-8">
        <h1 className={`text-3xl font-bold mb-8 text-center ${darkMode ? 'text-white' : 'text-gray-800'}`}>Operations</h1>
      
      {/* Gross Profit Margin Data and Goals Row */}
      <div className="grid grid-cols-4 gap-6 mb-6">
        {/* GPM Chart - 3/4 width */}
        <div className="col-span-3">
          <div className={`${darkMode ? 'bg-gray-800' : 'bg-white'} p-6 rounded-lg shadow-md border ${darkMode ? 'border-gray-700' : 'border-gray-100'}`}>
            <div className="flex items-center justify-center mb-4">
              {/* Centered Title */}
              <div className="flex items-center">
                <FaChartLine className="text-red-600 dark:text-red-400 mr-2" size={24} />
                <h2 className={`text-xl font-semibold ${darkMode ? 'text-white' : 'text-gray-800'}`}>Gross Profit Margin</h2>
              </div>
            </div>
            
            {showGpmForm ? (
              /* GPM Input Form */
              <div className="mt-6">
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
                  <div>
                    <label className={`block text-sm font-medium mb-2 ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                      GPM {new Date().getFullYear() - 2}
                    </label>
                    <div className="relative">
                      <span className="absolute left-3 top-3 text-gray-500 dark:text-gray-400">%</span>
                      <input
                        type="text"
                        name="twoYearsAgo"
                        value={gpmForm.twoYearsAgo}
                        onChange={handleGpmFormChange}
                        className={`pl-7 py-3 block w-full rounded-lg border-2 border-gray-200 dark:border-gray-600 shadow-sm focus:border-red-500 focus:ring-2 focus:ring-red-200 dark:focus:ring-red-800 dark:bg-gray-700 dark:text-white sm:text-sm transition-all duration-200 hover:border-gray-300 dark:hover:border-gray-500`}
                        placeholder={`Enter ${new Date().getFullYear() - 2} GPM %`}
                      />
                    </div>
                  </div>
                  <div>
                    <label className={`block text-sm font-medium mb-2 ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                      GPM {new Date().getFullYear() - 1}
                    </label>
                    <div className="relative">
                      <span className="absolute left-3 top-3 text-gray-500 dark:text-gray-400">%</span>
                      <input
                        type="text"
                        name="oneYearAgo"
                        value={gpmForm.oneYearAgo}
                        onChange={handleGpmFormChange}
                        className={`pl-7 py-3 block w-full rounded-lg border-2 border-gray-200 dark:border-gray-600 shadow-sm focus:border-red-500 focus:ring-2 focus:ring-red-200 dark:focus:ring-red-800 dark:bg-gray-700 dark:text-white sm:text-sm transition-all duration-200 hover:border-gray-300 dark:hover:border-gray-500`}
                        placeholder={`Enter ${new Date().getFullYear() - 1} GPM %`}
                      />
                    </div>
                  </div>
                  <div>
                    <label className={`block text-sm font-medium mb-2 ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                      {new Date().getFullYear()} Projection
                    </label>
                    <div className="relative">
                      <span className="absolute left-3 top-3 text-gray-500 dark:text-gray-400">%</span>
                      <input
                        type="text"
                        name="currentYearProjection"
                        value={gpmForm.currentYearProjection}
                        onChange={handleGpmFormChange}
                        className={`pl-7 py-3 block w-full rounded-lg border-2 border-gray-200 dark:border-gray-600 shadow-sm focus:border-red-500 focus:ring-2 focus:ring-red-200 dark:focus:ring-red-800 dark:bg-gray-700 dark:text-white sm:text-sm transition-all duration-200 hover:border-gray-300 dark:hover:border-gray-500`}
                        placeholder={`Enter ${new Date().getFullYear()} projection %`}
                      />
                    </div>
                  </div>
                </div>
                <div className="flex justify-center">
                  <button
                    onClick={handleGpmSubmit}
                    className="inline-flex items-center px-6 py-3 border border-transparent text-base font-medium rounded-md text-white bg-red-600 hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500"
                  >
                    Save
                  </button>
                </div>
              </div>
            ) : (
              /* GPM Chart Display */
              <div className="mt-6">
                <div className="h-[400px] mb-4">
                  <ResponsiveContainer width="100%" height="100%">
                    <LineChart data={generateGpmChartData()} margin={{ top: 5, right: 30, left: 20, bottom: 5 }}>
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis dataKey="year" />
                      <YAxis />
                      <Tooltip 
                        formatter={(value) => [value + '%', 'GPM']}
                        labelFormatter={(label) => `Year: ${label}`}
                      />
                      <Line 
                        type="monotone" 
                        dataKey="value" 
                        name="GPM" 
                        stroke="#DC2626" 
                        strokeWidth={2}
                        dot={{ r: 6 }}
                        activeDot={{ r: 8 }}
                      />
                    </LineChart>
                  </ResponsiveContainer>
                </div>
                <div className="flex justify-center space-x-4">
                  <button
                    onClick={handleEditGpm}
                    className="inline-flex items-center px-4 py-2 border border-gray-300 dark:border-gray-600 shadow-sm text-sm font-medium rounded-md text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-800 hover:bg-gray-50 dark:hover:bg-gray-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500"
                  >
                    Edit
                  </button>
                  <button
                    onClick={handleContinue}
                    className="inline-flex items-center px-4 py-2 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-red-600 hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500"
                  >
                    Continue
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Goals Card - 1/4 width */}
        <div className="col-span-1">
          <div className={`${darkMode ? 'bg-gray-800' : 'bg-white'} p-6 rounded-lg shadow-md border ${darkMode ? 'border-gray-700' : 'border-gray-100'} hover:shadow-lg transition-shadow duration-200`}>
            <div className="flex items-center justify-center mb-4">
              <FaBullseye className="text-2xl text-red-600 mr-2" />
              <h2 className={`text-xl font-semibold ${darkMode ? 'text-white' : 'text-gray-800'}`}>Goals</h2>
            </div>
            <div className="space-y-4">
              <div className="flex flex-col items-center justify-center min-h-[100px]">
                <FaLock className="w-12 h-12 text-gray-400 dark:text-gray-500 mb-2" />
              </div>
            </div>
            
            <div className="w-full mt-4 pt-4 border-t border-gray-200 dark:border-gray-700">
              <span className="w-full px-4 py-2 text-sm text-gray-400 dark:text-gray-500 font-medium rounded-lg transition-colors block text-center">
                View All Goals
              </span>
            </div>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-6">
        {operationsItems.map((item, index) => (
          <div 
            key={index}
            className={`${darkMode ? 'bg-gray-800' : 'bg-white'} p-6 rounded-lg shadow-md border ${darkMode ? 'border-gray-700' : 'border-gray-100'} relative`}
          >
            {/* Lock icon in top-right corner */}
            <div className="absolute top-3 right-3">
              <FaLock className="text-gray-400 dark:text-gray-500 text-sm" />
            </div>
            
            <div className="flex flex-col items-center text-center">
              <div className="text-4xl text-red-600 mb-2">
                {item.icon}
              </div>
              <h3 className={`text-xl font-semibold mb-2 ${darkMode ? 'text-white' : 'text-gray-800'}`}>{item.title}</h3>
              <p className={darkMode ? 'text-gray-400' : 'text-gray-600'}>{item.description}</p>
            </div>
          </div>
        ))}
      </div>

      {/* Schedule Call and Ask a Question Row */}
      <div className="grid grid-cols-2 gap-6">
        <div className={`${darkMode ? 'bg-gray-800' : 'bg-white'} p-6 rounded-lg shadow-md border ${darkMode ? 'border-gray-700' : 'border-gray-100'} relative`}>
          {/* Lock icon in top-right corner */}
          <div className="absolute top-3 right-3">
            <FaLock className="text-gray-400 dark:text-gray-500 text-sm" />
          </div>
          
          <div className="flex items-center justify-center mb-4">
            <FaCalendarAlt className="text-4xl text-red-600" />
          </div>
          <h2 className={`text-xl font-semibold text-center mb-4 ${darkMode ? 'text-white' : 'text-gray-800'}`}>Schedule a Call</h2>
          <div className={`p-4 ${darkMode ? 'bg-gray-700' : 'bg-gray-50'} rounded-lg text-center`}>
            <p className={`text-sm ${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>Book a consultation</p>
          </div>
        </div>

        <div className={`${darkMode ? 'bg-gray-800' : 'bg-white'} p-6 rounded-lg shadow-md border ${darkMode ? 'border-gray-700' : 'border-gray-100'} relative`}>
          {/* Lock icon in top-right corner */}
          <div className="absolute top-3 right-3">
            <FaLock className="text-gray-400 dark:text-gray-500 text-sm" />
          </div>
          
          <div className="flex items-center justify-center mb-4">
            <FaUserCog className="text-4xl text-red-600" />
          </div>
          <h2 className={`text-xl font-semibold text-center mb-4 ${darkMode ? 'text-white' : 'text-gray-800'}`}>Ask a Question</h2>
          <div className={`p-4 ${darkMode ? 'bg-gray-700' : 'bg-gray-50'} rounded-lg text-center`}>
            <p className={`text-sm ${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>Get help from our team</p>
          </div>
        </div>
      </div>
      </div>
    </div>
  );
};

export default GPMOperations;
